import { useState, useEffect } from 'react'
import { Link, useParams, useNavigate } from 'react-router-dom'
import {
  Grid,
  Typography,
  Avatar,
  List,
  ListItem,
  ListItemText,
  Box,
  Tooltip,
  Chip,
  Button,
  CircularProgress,
  useTheme,
  useMediaQuery,
} from '@mui/material'
import {
  Print as PrintIcon,
} from '@mui/icons-material'
import {
  PageContainer,
  StyledCard,
  SectionHeader,
  PrimaryButton,
} from '../components/ui/StyledComponents'
import supabase from '../supabaseClient'
import StudentQuranProgressChart from '../components/StudentQuranProgressChart'
import StudentAttendanceChart from '../components/StudentAttendanceChart'
import StudentNotes from '../components/StudentNotes'
import ProgressModeToggle from '../components/ui/ProgressModeToggle'
import EditStudentForm from '../components/admin/EditStudentForm'
import StudentPrintView from '../components/StudentPrintView'
import ArchiveDialog from '../components/admin/ArchiveDialog'
import SnackbarMessage from '../components/SnackbarMessage'
import { useAuth } from '../context/AuthContext'

function StudentDetailPage() {
  const { studentId } = useParams()
  const navigate = useNavigate()
  const [student, setStudent] = useState(null)
  const [classes, setClasses] = useState([]) // State for multiple classes
  const [guardians, setGuardians] = useState([]) // State for multiple guardians
  const [progressMode, setProgressMode] = useState('verses')
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false)
  const [isPrintDialogOpen, setIsPrintDialogOpen] = useState(false)
  const [archiveDialogOpen, setArchiveDialogOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  })
  const { userRole, user } = useAuth()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const isAdmin = userRole === 'admin' || userRole === 'super_admin'

  // Fetch student demographic info
  useEffect(() => {
    const fetchStudentInfo = async () => {
      const { data, error } = await supabase
        .from('students')
        .select('*')
        .eq('id', studentId)
        .single()

      if (error) {
        console.error('Error fetching student info:', error)
      } else {
        setStudent(data)
      }
    }

    fetchStudentInfo()
  }, [studentId])

  // Fetch class data for the student (based on class_ids array)
  useEffect(() => {
    const fetchClassInfo = async () => {
      if (student?.class_ids && student.class_ids.length > 0) {
        // Fetch all classes that match the IDs in the student's class_ids array
        const { data, error } = await supabase
          .from('classes')
          .select('*')
          .in('id', student.class_ids) // Use .in() to match multiple IDs

        if (error) {
          console.error('Error fetching class info:', error)
        } else {
          setClasses(data) // Set multiple classes in state
        }
      }
    }

    if (student) {
      fetchClassInfo()
    }
  }, [student])

  // Fetch guardian information using junction table
  useEffect(() => {
    const fetchGuardianInfo = async () => {
      if (student?.id) {
        const { data, error } = await supabase
          .from('student_guardians')
          .select(`
            *,
            guardians (*)
          `)
          .eq('student_id', student.id)
          .order('is_primary', { ascending: false })

        if (error) {
          console.error('Error fetching guardian info:', error)
        } else {
          setGuardians(data || [])
        }
      }
    }

    if (student) {
      fetchGuardianInfo()
    }
  }, [student])

  // Add this function to handle messaging
  /* Comment out messaging functionality
  const handleSendMessage = () => {
    // Navigate to messages page with student info pre-selected
    navigate('/messages', { 
      state: { 
        newConversation: true, 
        recipient: {
          id: student.id,
          name: `${student.first_name} ${student.last_name}`,
          type: 'student'
        }
      } 
    })
  }
  */

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (event) => {
      // Check for Ctrl+P or Cmd+P (depending on platform)
      if ((event.ctrlKey || event.metaKey) && event.key === 'p') {
        event.preventDefault() // Prevent default browser print dialog
        if (student) {
          setIsPrintDialogOpen(true)
        }
      }
    }

    // Add event listener when component mounts
    document.addEventListener('keydown', handleKeyDown)

    // Cleanup event listener when component unmounts
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [student]) // Include student in dependency array so it's available in the handler

  const handleEditSuccess = () => {
    // Refresh student data
    const fetchStudentInfo = async () => {
      const { data, error } = await supabase
        .from('students')
        .select('*')
        .eq('id', studentId)
        .single()

      if (error) {
        console.error('Error fetching student info:', error)
      } else {
        setStudent(data)
      }
    }

    fetchStudentInfo()
  }

  const handleToggleActive = () => {
    if (!student?.id) return

    // If currently active and trying to inactivate, show archive dialog
    if (student.is_active !== false) {
      setArchiveDialogOpen(true)
    } else {
      // If reactivating, proceed directly (no dialog needed)
      handleReactivate()
    }
  }

  const handleArchiveConfirm = async (reason) => {
    if (!student?.id) return

    setArchiveDialogOpen(false)
    setLoading(true)

    try {
      const { error: updateError } = await supabase
        .from('students')
        .update({
          is_active: false,
          archive_reason: reason,
          archived_by: user?.id || null,
          archived_at: new Date().toISOString(),
        })
        .eq('id', student.id)

      if (updateError) throw updateError

      // Refresh student data
      const { data, error: fetchError } = await supabase
        .from('students')
        .select('*')
        .eq('id', student.id)
        .single()

      if (fetchError) throw fetchError
      setStudent(data)

      setSnackbar({
        open: true,
        message: 'Student inactivated successfully',
        severity: 'success',
      })
    } catch (error) {
      console.error('Error inactivating student:', error)
      setSnackbar({
        open: true,
        message: 'Failed to inactivate student',
        severity: 'error',
      })
    } finally {
      setLoading(false)
    }
  }

  const handleReactivate = async () => {
    if (!student?.id) return

    setLoading(true)

    try {
      const { error: updateError } = await supabase
        .from('students')
        .update({
          is_active: true,
          archive_reason: null,
          archived_by: null,
          archived_at: null,
        })
        .eq('id', student.id)

      if (updateError) throw updateError

      // Refresh student data
      const { data, error: fetchError } = await supabase
        .from('students')
        .select('*')
        .eq('id', student.id)
        .single()

      if (fetchError) throw fetchError
      setStudent(data)

      setSnackbar({
        open: true,
        message: 'Student reactivated successfully',
        severity: 'success',
      })
    } catch (error) {
      console.error('Error reactivating student:', error)
      setSnackbar({
        open: true,
        message: 'Failed to reactivate student',
        severity: 'error',
      })
    } finally {
      setLoading(false)
    }
  }

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false })
  }

  return (
    <PageContainer>
      {student ? (
        <>
          {/* Page Header with Actions */}
          <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between', 
            alignItems: { xs: 'flex-start', sm: 'center' }, 
            mb: { xs: 2, sm: 3 },
            gap: { xs: 1.5, sm: 0 },
          }}>
            <Typography 
              variant="h4" 
              component="h1"
              sx={{ 
                fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' },
              }}
            >
              {student.first_name} {student.last_name}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, width: { xs: '100%', sm: 'auto' } }}>
              <Tooltip title="Print Student Report">
                <PrimaryButton
                  onClick={() => setIsPrintDialogOpen(true)}
                  startIcon={<PrintIcon sx={{ fontSize: { xs: '1.125rem', sm: '1.25rem' } }} />}
                  fullWidth={isMobile}
                  sx={{ 
                    backgroundColor: 'secondary.main',
                    color: 'secondary.contrastText',
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                    padding: { xs: '10px 16px', sm: '12px 24px' },
                    '&:hover': {
                      backgroundColor: 'secondary.dark'
                    }
                  }}
                >
                  Print
                </PrimaryButton>
              </Tooltip>
            </Box>
          </Box>

          <Grid container spacing={{ xs: 2, sm: 3 }}>
            {/* Student Demographic Information */}
            <Grid item xs={12} sm={4}>
            <StyledCard sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
              <SectionHeader sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem', md: '1.75rem' }, mb: { xs: 1.5, sm: 2 } }}>
                Student Info
              </SectionHeader>
              <Box sx={{ display: 'flex', justifyContent: { xs: 'center', sm: 'flex-start' }, mb: { xs: 1.5, sm: 2 } }}>
                <Avatar
                  alt={`${student.first_name} ${student.last_name}`}
                  src={student.photo_url}
                  sx={{ 
                    width: { xs: 100, sm: 110, md: 120 }, 
                    height: { xs: 100, sm: 110, md: 120 },
                  }}
                />
              </Box>
              <Typography 
                variant="h6"
                sx={{ 
                  fontSize: { xs: '1.125rem', sm: '1.25rem', md: '1.5rem' },
                  textAlign: { xs: 'center', sm: 'left' },
                }}
              >
                {student.first_name} {student.last_name}
              </Typography>
              <Typography 
                variant="body1" 
                sx={{ 
                  mt: 1,
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                  textAlign: { xs: 'center', sm: 'left' },
                }}
              >
                Age: {student.date_of_birth}
              </Typography>
              <Typography 
                variant="body1" 
                sx={{ 
                  mt: 1,
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                  textAlign: { xs: 'center', sm: 'left' },
                }}
              >
                Gender: {student.gender}
              </Typography>
              {student.is_self_guardian && (
                <Box sx={{ display: 'flex', justifyContent: { xs: 'center', sm: 'flex-start' }, mt: 1 }}>
                  <Chip 
                    label="Self Guardian" 
                    color="primary"
                    sx={{
                      fontSize: { xs: '0.7rem', sm: '0.75rem' },
                      height: { xs: 20, sm: 24 },
                    }}
                  />
                </Box>
              )}
              {isAdmin && (
                <>
                  <PrimaryButton
                    variant="contained"
                    onClick={() => setIsEditDialogOpen(true)}
                    fullWidth
                    sx={{ 
                      mt: 2,
                      fontSize: { xs: '0.875rem', sm: '1rem' },
                      padding: { xs: '10px 16px', sm: '12px 24px' },
                    }}
                  >
                    Edit Student Info
                  </PrimaryButton>
                  <Button
                    variant={student.is_active !== false ? 'outlined' : 'contained'}
                    color={student.is_active !== false ? 'error' : 'success'}
                    onClick={handleToggleActive}
                    disabled={loading}
                    fullWidth
                    sx={{ 
                      mt: 1,
                      fontSize: { xs: '0.875rem', sm: '1rem' },
                      padding: { xs: '10px 16px', sm: '12px 24px' },
                    }}
                  >
                    {loading ? (
                      <CircularProgress size={isMobile ? 20 : 24} />
                    ) : student.is_active !== false ? (
                      'Inactivate Student'
                    ) : (
                      'Reactivate Student'
                    )}
                  </Button>
                  {/* Account Status */}
                  <Box sx={{ 
                    mt: 2, 
                    p: { xs: 1.5, sm: 2 }, 
                    border: '1px solid', 
                    borderColor: 'divider', 
                    borderRadius: 1 
                  }}>
                    <Typography 
                      variant="subtitle2" 
                      color="text.secondary" 
                      gutterBottom
                      sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                    >
                      Account Status
                    </Typography>
                    <Chip
                      label={student.is_active !== false ? 'Active' : 'Inactive'}
                      color={student.is_active !== false ? 'success' : 'default'}
                      size="small"
                      sx={{ 
                        mt: 1,
                        fontSize: { xs: '0.7rem', sm: '0.75rem' },
                        height: { xs: 20, sm: 24 },
                      }}
                    />
                  </Box>
                </>
              )}
            </StyledCard>
          </Grid>

          {/* Parent/Guardian Information - Updated for multiple guardians */}
          <Grid item xs={12} sm={4}>
            <StyledCard sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
              <SectionHeader sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem', md: '1.75rem' }, mb: { xs: 1.5, sm: 2 } }}>
                Parent/Guardian Info
              </SectionHeader>
              {guardians && guardians.length > 0 ? (
                <List dense sx={{ mt: 1 }}>
                  {guardians.map((sg) => (
                    <ListItem key={sg.id} sx={{ px: 0, py: { xs: 0.75, sm: 1 } }}>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                            <Link
                              to={`/parent_details/${sg.guardians.id}`}
                              style={{
                                color: 'var(--primary-light)',
                                textDecoration: 'none',
                                fontWeight: 'bold',
                                fontSize: isMobile ? '0.875rem' : '1rem',
                              }}
                            >
                              {`${sg.guardians.first_name} ${sg.guardians.last_name}`}
                            </Link>
                            {sg.is_primary && (
                              <Chip 
                                label="Primary" 
                                size="small" 
                                color="primary"
                                sx={{
                                  fontSize: { xs: '0.65rem', sm: '0.75rem' },
                                  height: { xs: 18, sm: 20 },
                                }}
                              />
                            )}
                          </Box>
                        }
                        secondary={
                          <Box sx={{ mt: 0.5 }}>
                            <Typography 
                              variant="body2" 
                              color="textSecondary"
                              sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                            >
                              {sg.relationship_type}
                            </Typography>
                            <Typography 
                              variant="body2" 
                              color="textSecondary"
                              sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                            >
                              {sg.guardians.phone || 'No phone'}
                            </Typography>
                            <Typography 
                              variant="body2" 
                              color="textSecondary"
                              sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                            >
                              {sg.guardians.email || 'No email'}
                            </Typography>
                          </Box>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography 
                  variant="body1" 
                  sx={{ 
                    mt: 1,
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                  }}
                >
                  No guardian information available
                </Typography>
              )}
            </StyledCard>
          </Grid>

                    {/* Classes Info - Updated to show multiple classes */}
                    <Grid item xs={12} sm={4}>
            <StyledCard sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
              <SectionHeader sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem', md: '1.75rem' }, mb: { xs: 1.5, sm: 2 } }}>
                All Classes
              </SectionHeader>
              {classes.length > 0 ? (
                <List
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    flexWrap: 'wrap',
                  }}
                >
                  {classes.map((classData, index) => (
                    <ListItem
                      key={classData.id}
                      sx={{
                        width: { xs: '100%', sm: 'auto' },
                        padding: { xs: '8px 12px', sm: '10px 15px' },
                        borderRight: { 
                          xs: 'none',
                          sm: index < classes.length - 1 ? '1px solid #ddd' : 'none',
                        },
                        borderBottom: {
                          xs: index < classes.length - 1 ? '1px solid #ddd' : 'none',
                          sm: 'none',
                        },
                      }}
                    >
                      <ListItemText
                        primary={
                          <Typography sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                            {classData.name}
                          </Typography>
                        }
                        secondary={
                          <Link
                            to={`/teacher_details/${classData.teacher_id}`}
                            style={{
                              color: 'var(--primary-light)',
                              textDecoration: 'none',
                              fontWeight: 'bold',
                              fontSize: isMobile ? '0.75rem' : '0.875rem',
                            }}
                          >
                            View Teacher Details
                          </Link>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography 
                  variant="body1"
                  sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                >
                  No classes assigned to this student
                </Typography>
              )}
            </StyledCard>
          </Grid>

          {/* Attendance Chart */}
          <Grid item xs={12} md={6}>
            <StyledCard sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
              <SectionHeader sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem', md: '1.75rem' }, mb: { xs: 1.5, sm: 2 } }}>
                Attendance Overview
              </SectionHeader>
              <StudentAttendanceChart studentId={studentId} />
            </StyledCard>
          </Grid>

          {/* Quran Progress Chart */}
          <Grid item xs={12} md={6}>
            <StyledCard sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: { xs: 1.5, sm: 2 }, flexWrap: 'wrap', gap: 1 }}>
                <SectionHeader sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem', md: '1.75rem' }, mb: 0 }}>
                  Quran Progress
                </SectionHeader>
                <ProgressModeToggle value={progressMode} onChange={(event, newValue) => {
                  if (newValue !== null) {
                    setProgressMode(newValue);
                  }
                }} />
              </Box>
              <StudentQuranProgressChart studentId={studentId} progressMode={progressMode} />
            </StyledCard>
          </Grid>
          
          {/* Student Notes History */}
          <Grid item xs={12}>
            <StyledCard sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
              <SectionHeader sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem', md: '1.75rem' }, mb: { xs: 1.5, sm: 2 } }}>
                Notes History
              </SectionHeader>
              <StudentNotes studentId={studentId} showAddNote={false} />
            </StyledCard>
          </Grid>

          {/* Waitlist Link - Admin Only */}
          {isAdmin && (
            <Grid item xs={12}>
              <StyledCard sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: { xs: 'column', sm: 'row' },
                  justifyContent: 'space-between', 
                  alignItems: { xs: 'flex-start', sm: 'center' },
                  gap: { xs: 1.5, sm: 0 },
                }}>
                  <SectionHeader sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem', md: '1.75rem' }, mb: 0 }}>
                    Waitlist
                  </SectionHeader>
                  <PrimaryButton
                    variant="contained"
                    onClick={() => navigate(`/admin/waitlist?student=${studentId}`)}
                    fullWidth={isMobile}
                    sx={{
                      fontSize: { xs: '0.875rem', sm: '1rem' },
                      padding: { xs: '10px 16px', sm: '12px 24px' },
                    }}
                  >
                    View Waitlist
                  </PrimaryButton>
                </Box>
                <Typography 
                  variant="body2" 
                  color="text.secondary" 
                  sx={{ 
                    mt: 1,
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                  }}
                >
                  Manage this student's waitlist entries from the Waitlist Management page.
                </Typography>
              </StyledCard>
            </Grid>
          )}
        </Grid>
        </>
      ) : (
        <StyledCard sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
          <Typography 
            variant="body1"
            sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
          >
            Loading student details...
          </Typography>
        </StyledCard>
      )}

      {/* Edit Dialog */}
      <EditStudentForm
        student={student}
        open={isEditDialogOpen}
        onClose={() => setIsEditDialogOpen(false)}
        onSuccess={handleEditSuccess}
      />

      {/* Print Dialog */}
      <StudentPrintView
        student={student}
        classes={classes}
        guardians={guardians}
        open={isPrintDialogOpen}
        onClose={() => setIsPrintDialogOpen(false)}
      />

      {/* Archive Dialog */}
      <ArchiveDialog
        open={archiveDialogOpen}
        onClose={() => setArchiveDialogOpen(false)}
        onConfirm={handleArchiveConfirm}
        entityName="Student"
      />

      {/* Snackbar */}
      <SnackbarMessage
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={handleSnackbarClose}
      />
    </PageContainer>
  )
}

export default StudentDetailPage
